'use client'

import { SortingState } from '@tanstack/react-table'
import { useSearchParams, usePathname } from 'next/navigation'
import { useMemo, useState, useEffect } from 'react'

type Direction = 'asc' | 'desc'

interface Options<Q> {
  /** Raw query coming from parent (filters, pagination, …) */
  query: Q
  /** If true we’ll ask the API for sorted data instead of sorting locally. */
  isServerSorting?: boolean
  /** Keep ?field= & ?direction= in the browser url (default: true). */
  syncWithUrl?: boolean
  paramPrefix?: string
}

export const useTableSorting = <Q>({ query, isServerSorting = true, syncWithUrl = true, paramPrefix }: Options<Q>) => {
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const fieldKey = paramPrefix ? `${paramPrefix}.field` : 'field'
  const directionKey = paramPrefix ? `${paramPrefix}.direction` : 'direction'

  const initialSorting = useMemo<SortingState>(() => {
    if (!syncWithUrl) return []
    const field = searchParams.get(fieldKey)
    const dir = searchParams.get(directionKey)

    return field ? [{ id: field, desc: dir === 'desc' }] : []
  }, [searchParams, syncWithUrl, fieldKey, directionKey])

  const [sorting, setSorting] = useState<SortingState>(initialSorting)

  const queryWithSorting = useMemo(() => {
    if (!isServerSorting || sorting.length === 0) return query

    const { id, desc } = sorting[0] ?? {}

    if (id && typeof id === 'string') {
      return {
        ...query,
        field: id.toString(),
        direction: (desc ? 'desc' : 'asc') as Direction,
      } as Q & { field: string; direction: Direction }
    }

    return query
  }, [query, sorting, isServerSorting])

  /* ---------------- keep url in sync (optional) */
  useEffect(() => {
    if (!syncWithUrl || !isServerSorting) return

    const params = new URLSearchParams(window.location.search)
    const { id, desc } = sorting.length ? (sorting[0] ?? {}) : {}

    if (id && typeof id === 'string') {
      params.set(fieldKey, id.toString())
      params.set(directionKey, desc ? 'desc' : 'asc')
    } else {
      params.delete(fieldKey)
      params.delete(directionKey)
    }

    // Fix: issue with adding empty single "?", then removing it and adding it again in infinite cycle
    const newParams = params.toString()
    // Preserve any URL hash (e.g. `#selected_id=…` driving a detail sidebar).
    // Without this, a bare `pathname?params` strips the hash on mount whenever
    // filters/search params are present — closing shared/deep-linked sidebars.
    const { hash } = window.location
    const newFullPath = `${pathname}?${newParams}${hash}`

    const currentFullPath = `${window.location.pathname}${window.location.search}${hash}`

    if (newParams.length > 0 && newFullPath !== currentFullPath) {
      window.history.replaceState(null, '', newFullPath)
    }
  }, [sorting, syncWithUrl, isServerSorting, pathname, searchParams, fieldKey, directionKey])

  return {
    /* expose to <Table /> */
    sorting,
    setSorting, // in case the consumer wants to imperatively change it
    onSortingChange: setSorting,
    queryWithSorting,
  }
}
